import { Link, useParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import PageMeta from "../components/PageMeta";
import CTABand from "../components/CTABand";
import { POSTS } from "../data/content";

const Article = () => {
  const { slug } = useParams();
  const post = POSTS.find((p) => p.slug === slug);

  if (!post) {
    return (
      <main className="flex min-h-[70vh] items-center justify-center pt-24" data-testid="article-not-found">
        <div className="text-center">
          <p className="font-heading text-2xl font-semibold text-ink">Article not found.</p>
          <Link to="/resources" className="btn-outline mt-6">Back to resources</Link>
        </div>
      </main>
    );
  }

  const related = POSTS.filter((p) => p.slug !== slug).slice(0, 3);

  return (
    <main className="pt-[76px]" data-testid="article-page">
      <PageMeta title={`${post.seo_title || post.title} | AccuSurge Resources`} description={post.seo_description || post.excerpt} />
      <article>
        <header className="container-x max-w-4xl pt-12 md:pt-20">
          <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-accent-600">{post.category}</span>
          <h1 className="mt-4 font-heading text-3xl md:text-5xl font-semibold tracking-tighter text-ink text-balance" data-testid="article-title">{post.title}</h1>
          <p className="mt-5 text-sm text-slate-400" data-testid="article-meta">By {post.author} · AccuSurge Resource Centre</p>
        </header>
        <div className="container-x max-w-4xl">
          <img src={post.image} alt={post.title} className="mt-10 h-[280px] w-full rounded-2xl object-cover md:h-[420px]" />
        </div>
        <div className="container-x max-w-4xl py-12 md:py-16">
          <div data-testid="article-content">
            {post.content.split("\n\n").map((block, i) =>
              block.startsWith("## ") ? (
                <h2 key={i} className="mt-10 font-heading text-2xl font-semibold tracking-tight text-ink">
                  {block.replace("## ", "")}
                </h2>
              ) : (
                <p key={i} className="mt-5 text-base md:text-lg leading-relaxed text-slate-700">{block}</p>
              )
            )}
          </div>
          <div className="mt-14 rounded-2xl bg-brand-900 p-8 md:p-10" data-testid="article-cta">
            <h2 className="font-heading text-xl md:text-2xl font-semibold text-white">Planning a facility or equipment upgrade?</h2>
            <p className="mt-2 text-sm text-white/70">Our team can map requirements for your project — from a single department to a complete hospital.</p>
            <Link to="/contact" className="btn-accent mt-6">Talk to Our Healthcare Expert <ArrowRight className="h-4 w-4" /></Link>
          </div>
        </div>
      </article>

      {related.length > 0 && (
        <section className="border-t border-slate-200 py-16" data-testid="related-articles">
          <div className="container-x max-w-5xl">
            <h2 className="font-heading text-2xl font-semibold text-ink">More from the Resource Centre</h2>
            <div className="mt-8 grid gap-6 md:grid-cols-3">
              {related.map((p) => (
                <Link key={p.id} to={`/resources/${p.slug}`} className="group" data-testid={`related-article-${p.slug}`}>
                  <div className="h-36 overflow-hidden rounded-lg">
                    <img src={p.image} alt={p.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <h3 className="mt-3 font-heading text-base font-semibold leading-snug text-ink group-hover:text-brand-700">{p.title}</h3>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <CTABand />
    </main>
  );
};

export default Article;
